import { useProctoredAttempt } from '../../components/focus-guard/FocusGuardContext';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { MainLayout } from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Skeleton } from '../../components/ui/skeleton';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import useContentProtection from '../../hooks/useContentProtection';
import AssessmentFeedback from '../../components/rpat/AssessmentFeedback';
import { ResultSummary } from '../../components/common/ResultSummary';
import { rpatPath, rpatStatus, rpatStatusDetails, pct, shortDate } from '../../components/rpat/assessmentResult';
import { 
  Play,
  Mic,
  MicOff,
  CheckCircle2,
  XCircle,
  Clock,
  Target,
  ArrowLeft,
  ArrowRight,
  Award,
  RefreshCw,
  Volume2,
  Brain
} from 'lucide-react';

const RPATAssessment = () => {
  const { assessmentId } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  
  // Apply content protection - no screenshots, copy, right-click
  useContentProtection(true);
  
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [assessment, setAssessment] = useState(null);
  const [attemptId, setAttemptId] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [feedback, setFeedback] = useState({});
  const [isRecording, setIsRecording] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  useProctoredAttempt({ active: Boolean(attemptId) && !result, label: 'assessment', onForceEnd: () => completeAssessment() });
  const [startTime, setStartTime] = useState(null);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const speechSynthRef = useRef(window.speechSynthesis);
  const attemptIdRef = useRef(null);
  const currentQuestionRef = useRef(null);

  // Derived values - must be declared before useEffects that depend on them
  const currentQuestion = assessment?.questions?.[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === (assessment?.questions?.length || 0) - 1;

  // Keep refs updated
  useEffect(() => {
    attemptIdRef.current = attemptId;
  }, [attemptId]);
  
  useEffect(() => {
    currentQuestionRef.current = currentQuestion;
  }, [currentQuestion]);

  const fetchAssessment = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(false);
      const res = await api.get(`/rpat/assessments/${assessmentId}`);
      setAssessment(res.data);
    } catch (err) {
      console.error('Failed to fetch assessment:', err);
      toast.error('Failed to load assessment');
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [api, assessmentId]);

  useEffect(() => {
    fetchAssessment();
  }, [fetchAssessment]);

  const startAssessment = async () => {
    try {
      const res = await api.post(`/rpat/assessments/${assessmentId}/start`);
      setAttemptId(res.data.attempt_id);
      setStartTime(Date.now());
      setCurrentQuestionIndex(0);
      setAnswers({});
      setFeedback({});
      toast.success('Assessment started! Good luck!');
    } catch (err) {
      console.error('Failed to start assessment:', err);
      toast.error(err.response?.data?.detail || 'Failed to start assessment');
      fetchAssessment();
    }
  };

  const speakQuestion = () => {
    if (!currentQuestion) return;
    speechSynthRef.current.cancel();
    const utterance = new SpeechSynthesisUtterance(currentQuestion.voice_prompt || currentQuestion.question);
    utterance.rate = 0.9;
    speechSynthRef.current.speak(utterance);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (e) => {
        audioChunksRef.current.push(e.data);
      };
      
      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
        stream.getTracks().forEach(track => track.stop());
        // Use refs to avoid stale closure
        await submitVoiceAnswer(audioBlob, currentQuestionRef.current, attemptIdRef.current);
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (err) {
      console.error('Failed to start recording:', err);
      toast.error('Microphone access required for verbal responses');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const submitVoiceAnswer = async (audioBlob, question, attempt) => {
    // Use passed parameters to avoid stale closures
    const questionToUse = question || currentQuestionRef.current;
    const attemptToUse = attempt || attemptIdRef.current;
    
    if (!questionToUse || !attemptToUse) {
      toast.error('Session expired. Please restart the assessment.');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'answer.wav');
      
      const res = await api.post(
        `/rpat/assessments/${assessmentId}/submit-response`,
        formData,
        {
          params: {
            attempt_id: attemptToUse,
            question_id: questionToUse.id
          },
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      
      setFeedback(prev => ({
        ...prev,
        [questionToUse.id]: {
          is_correct: res.data.is_correct,
          feedback: res.data.feedback
        }
      }));
      
      setAnswers(prev => ({
        ...prev,
        [questionToUse.id]: '[Voice Response]'
      }));
      
    } catch (err) {
      console.error('Failed to submit voice answer:', err);
      toast.error('Failed to submit answer');
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitTextAnswer = async (answer) => {
    if (!currentQuestion || !attemptId) return;
    
    setIsSubmitting(true);
    try {
      const res = await api.post(
        `/rpat/assessments/${assessmentId}/submit-response`,
        null,
        {
          params: {
            attempt_id: attemptId,
            question_id: currentQuestion.id,
            user_answer: answer
          }
        }
      );
      
      setFeedback(prev => ({
        ...prev,
        [currentQuestion.id]: {
          is_correct: res.data.is_correct,
          feedback: res.data.feedback
        }
      }));
      
      setAnswers(prev => ({
        ...prev,
        [currentQuestion.id]: answer
      }));
      
    } catch (err) {
      console.error('Failed to submit answer:', err);
      toast.error('Failed to submit answer');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMultipleChoice = (value) => {
    setAnswers(prev => ({
      ...prev,
      [currentQuestion.id]: value
    }));
  };

  const submitCurrentAnswer = async () => {
    const answer = answers[currentQuestion.id];
    if (!answer) {
      toast.error('Please select or record an answer');
      return;
    }
    
    if (!feedback[currentQuestion.id]) {
      await submitTextAnswer(answer);
    }
  };

  const goToNext = async () => {
    // Submit current answer if not yet submitted
    if (!feedback[currentQuestion.id] && answers[currentQuestion.id]) {
      await submitCurrentAnswer();
    }
    
    if (!isLastQuestion) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const completeAssessment = async () => {
    try {
      setIsSubmitting(true);
      
      // Submit current answer if pending
      if (!feedback[currentQuestion.id] && answers[currentQuestion.id]) {
        await submitCurrentAnswer();
      }
      
      const res = await api.post(
        `/rpat/assessments/${assessmentId}/complete`,
        null,
        { params: { attempt_id: attemptId } }
      );
      
      setResult(res.data);
    } catch (err) {
      console.error('Failed to complete assessment:', err);
      toast.error(err.response?.data?.detail || 'Failed to complete assessment');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="p-6 space-y-6">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-96" />
        </div>
      </MainLayout>
    );
  }

  if (loadError && !assessment) {
    return (
      <MainLayout>
        <div className="p-6 max-w-2xl mx-auto">
          <Card data-testid="assessment-load-error">
            <CardContent className="py-10 text-center space-y-4">
              <XCircle className="h-10 w-10 mx-auto text-rose-500" />
              <p className="font-medium">This assessment could not be loaded.</p>
              <div className="flex justify-center gap-3">
                <Button variant="outline" onClick={() => navigate(-1)} data-testid="assessment-error-back-btn">
                  <ArrowLeft className="mr-2 h-4 w-4" />Go back
                </Button>
                <Button onClick={fetchAssessment} data-testid="assessment-error-retry-btn">
                  <RefreshCw className="mr-2 h-4 w-4" />Try again
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </MainLayout>
    );
  }

  // Result Screen
  if (result) {
    const canRetry = result.my_status?.can_start !== false;
    return (
      <MainLayout>
        <div className="p-6 max-w-3xl mx-auto space-y-6">
          {/* Score Summary Card */}
          <Card className={`overflow-hidden ${result.passed ? 'border-emerald-500' : 'border-amber-500'}`}>
            <div className={`p-8 text-center text-white ${
              result.passed 
                ? 'bg-gradient-to-r from-emerald-600 to-green-600' 
                : 'bg-gradient-to-r from-amber-600 to-orange-600'
            }`}>
              {result.passed ? (
                <Award className="h-16 w-16 mx-auto mb-4" />
              ) : canRetry ? (
                <RefreshCw className="h-16 w-16 mx-auto mb-4" />
              ) : (
                <Target className="h-16 w-16 mx-auto mb-4" />
              )}
              <h2 className="text-3xl font-bold mb-2" data-testid="rpat-result-heading">
                {result.passed ? 'Congratulations!' : canRetry ? 'Keep Learning!' : 'Final Result'}
              </h2>
              <p className="text-lg opacity-90">
                {result.passed 
                  ? 'You passed the assessment!' 
                  : canRetry ? `You need ${result.pass_percentage}% to pass. Try again!` : `You needed ${result.pass_percentage}% to pass.`
                }
              </p>
            </div>
            
            <CardContent className="pt-6">
              <div className="mb-6">
                <ResultSummary
                  testId="rpat-result-summary"
                  path={rpatPath(result.context)}
                  title={result.context?.assessment_title || assessment?.title}
                  score={result.percentage}
                  status={result.passed ? { label: 'Passed', tone: 'passed' } : { label: canRetry ? 'Not passed' : 'Not passed · no attempts left', tone: 'failed' }}
                  details={[
                    { label: 'Correct answers', value: `${result.questions_correct}/${result.total_questions}` },
                    { label: 'Attempt', value: result.my_status?.max_attempts ? `${result.my_status.attempts_used} of ${result.my_status.max_attempts}` : `#${result.attempt_number}` },
                    { label: 'Best score', value: pct(result.my_status?.best_score) },
                    { label: 'Pass mark', value: `${result.pass_percentage}%` },
                    { label: 'Completed', value: shortDate(result.completed_at) },
                  ]}
                />
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3">
                {result.passed ? (
                  <>
                    <Button 
                      className="flex-1 bg-gradient-to-r from-cyan-600 to-blue-600"
                      onClick={() => navigate('/rpat')}
                      data-testid="continue-learning-btn"
                    >
                      Continue Learning
                    </Button>
                    {result.topic_unlocked && (
                      <Button variant="outline" className="flex-1" onClick={() => navigate('/rpat')}>
                        Next Topic Unlocked!
                      </Button>
                    )}
                  </>
                ) : (
                  <>
                    {canRetry && (
                      <Button 
                        className="flex-1"
                        onClick={() => {
                          setResult(null);
                          setAttemptId(null);
                          setAnswers({});
                          setFeedback({});
                          fetchAssessment();
                        }}
                        data-testid="try-again-btn"
                      >
                        <RefreshCw className="mr-2 h-4 w-4" />
                        Try Again
                      </Button>
                    )}
                    <Button variant="outline" className="flex-1" onClick={() => navigate('/rpat')} data-testid="review-lesson-btn">
                      Review Lesson
                    </Button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* AI Feedback Section */}
          {result.result_id && (
            <AssessmentFeedback 
              resultId={result.result_id} 
              onClose={null}
            />
          )}
        </div>
      </MainLayout>
    );
  }

  // Start Screen
  if (!attemptId) {
    const ctx = assessment?.context;
    const myStatus = assessment?.my_status;
    const canStart = myStatus?.can_start !== false;
    return (
      <MainLayout>
        <div className="p-6 max-w-2xl mx-auto">
          <Card>
            <CardHeader className="text-center">
              <Target className="h-12 w-12 mx-auto text-cyan-600 mb-4" />
              {ctx?.phase_number && !(myStatus?.total_attempts > 0) && (
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground" data-testid="assessment-context">
                  {rpatPath(ctx).slice(1).join(' › ')}
                </p>
              )}
              <CardTitle className="text-2xl" data-testid="assessment-title">{assessment?.title}</CardTitle>
              <p className="text-muted-foreground">{assessment?.description}</p>
            </CardHeader>
            <CardContent>
              {myStatus?.total_attempts > 0 && (
                <div className="mb-6">
                  <ResultSummary
                    testId="rpat-attempt-summary"
                    path={rpatPath(ctx)}
                    title={assessment.title}
                    score={myStatus.best_score}
                    scoreLabel="Best score"
                    status={rpatStatus(myStatus)}
                    details={rpatStatusDetails(myStatus)}
                  />
                </div>
              )}

              <div className={`grid ${assessment?.max_attempts ? 'grid-cols-3' : 'grid-cols-2'} gap-4 mb-6`}>
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <p className="text-2xl font-bold">{assessment?.total_questions}</p>
                  <p className="text-sm text-muted-foreground">Questions</p>
                </div>
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <p className="text-2xl font-bold">{assessment?.pass_percentage}%</p>
                  <p className="text-sm text-muted-foreground">To Pass</p>
                </div>
                {assessment?.max_attempts && (
                  <div className="text-center p-4 bg-muted/50 rounded-lg">
                    <p className="text-2xl font-bold" data-testid="attempts-left">{myStatus?.attempts_left ?? assessment.max_attempts}</p>
                    <p className="text-sm text-muted-foreground">Attempts Left</p>
                  </div>
                )}
              </div>
              
              {canStart && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                  <h4 className="font-medium text-amber-800 mb-2">Assessment Guidelines</h4>
                  <ul className="text-sm text-amber-700 space-y-1">
                    <li>• Multiple choice questions: Say Option A, B, C, or D</li>
                    <li>• True/False questions: Say True or False</li>
                    <li>• Verbal questions: Explain your answer clearly</li>
                    <li>• You need {assessment?.pass_percentage}% to pass and unlock the next topic</li>
                  </ul>
                </div>
              )}
              
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={() => navigate(-1)} data-testid="back-to-lesson-btn">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Lesson
                </Button>
                {canStart ? (
                  <Button 
                    className="flex-1 bg-gradient-to-r from-cyan-600 to-blue-600"
                    onClick={startAssessment}
                    data-testid="start-assessment-btn"
                  >
                    <Play className="mr-2 h-4 w-4" />
                    {myStatus?.total_attempts > 0 ? 'Retake Assessment' : 'Start Assessment'}
                  </Button>
                ) : (
                  <Button className="flex-1" disabled data-testid="no-attempts-left-btn">
                    No attempts left
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </MainLayout>
    );
  }

  // Question Screen
  const questionFeedback = feedback[currentQuestion?.id];
  const currentAnswer = answers[currentQuestion?.id];
  const answeredCount = Object.keys(feedback).length;
  const progressPct = (answeredCount / assessment.total_questions) * 100;

  return (
    <MainLayout>
      <div className="p-6 max-w-3xl mx-auto">
        {/* Progress Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">
              Question {currentQuestionIndex + 1} of {assessment.total_questions}
            </span>
            <Badge variant="outline">
              {answeredCount} answered
            </Badge>
          </div>
          <Progress value={progressPct} className="h-2" />
        </div>

        <Card>
          <CardContent className="pt-6">
            {/* Question */}
            <div className="mb-6">
              <div className="flex items-start justify-between gap-4 mb-4">
                <h3 className="text-lg font-medium">{currentQuestion?.question}</h3>
                <Button variant="ghost" size="icon" onClick={speakQuestion} data-testid="speak-question-btn">
                  <Volume2 className="h-5 w-5" />
                </Button>
              </div>
              
              <Badge variant="secondary" className="mb-4">
                {currentQuestion?.type === 'multiple_choice' && 'Multiple Choice'}
                {currentQuestion?.type === 'true_false' && 'True or False'}
                {currentQuestion?.type === 'verbal_response' && 'Verbal Response'}
              </Badge>
            </div>

            {/* Answer Options */}
            {(currentQuestion?.type === 'multiple_choice' || currentQuestion?.type === 'true_false') && (
              <div className="mb-6">
                <RadioGroup
                  value={currentAnswer || ''}
                  onValueChange={handleMultipleChoice}
                  disabled={!!questionFeedback}
                >
                  {currentQuestion?.type === 'multiple_choice' ? (
                    currentQuestion?.options?.map((option, i) => (
                      <div key={`opt-${i}`} className="flex items-center space-x-3 p-3 rounded-lg hover:bg-muted/50">
                        <RadioGroupItem value={option.charAt(0)} id={`option-${i}`} data-testid={`answer-option-${i}`} />
                        <Label htmlFor={`option-${i}`} className="flex-1 cursor-pointer">
                          {option}
                        </Label>
                      </div>
                    ))
                  ) : (
                    <>
                      <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-muted/50">
                        <RadioGroupItem value="true" id="true" data-testid="answer-true" />
                        <Label htmlFor="true" className="cursor-pointer">True</Label>
                      </div>
                      <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-muted/50">
                        <RadioGroupItem value="false" id="false" data-testid="answer-false" />
                        <Label htmlFor="false" className="cursor-pointer">False</Label>
                      </div>
                    </>
                  )}
                </RadioGroup>
              </div>
            )}

            {/* Voice Recording for Verbal Response */}
            {currentQuestion?.type === 'verbal_response' && !questionFeedback && (
              <div className="mb-6 text-center">
                <p className="text-sm text-muted-foreground mb-4">
                  Click to record your verbal answer
                </p>
                <Button
                  size="lg"
                  variant={isRecording ? 'destructive' : 'default'}
                  className={`w-20 h-20 rounded-full ${!isRecording ? 'bg-gradient-to-r from-cyan-600 to-blue-600' : ''}`}
                  onClick={isRecording ? stopRecording : startRecording}
                  disabled={isSubmitting}
                  data-testid="record-answer-btn"
                >
                  {isRecording ? (
                    <MicOff className="h-8 w-8" />
                  ) : (
                    <Mic className="h-8 w-8" />
                  )}
                </Button>
                {isRecording && (
                  <p className="text-sm text-red-600 mt-2 animate-pulse">Recording... Click to stop</p>
                )}
              </div>
            )}

            {/* Feedback */}
            {questionFeedback && (
              <div className={`mb-6 p-4 rounded-lg ${
                questionFeedback.is_correct 
                  ? 'bg-emerald-50 border border-emerald-200' 
                  : 'bg-rose-50 border border-rose-200'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  {questionFeedback.is_correct ? (
                    <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                  ) : (
                    <XCircle className="h-5 w-5 text-rose-600" />
                  )}
                  <span className={`font-medium ${
                    questionFeedback.is_correct ? 'text-emerald-800' : 'text-rose-800'
                  }`}>
                    {questionFeedback.is_correct ? 'Correct!' : 'Incorrect'}
                  </span>
                </div>
                <p className={`text-sm ${
                  questionFeedback.is_correct ? 'text-emerald-700' : 'text-rose-700'
                }`}>
                  {questionFeedback.feedback}
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              {!questionFeedback && currentAnswer && (
                <Button 
                  className="flex-1"
                  onClick={submitCurrentAnswer}
                  disabled={isSubmitting}
                  data-testid="submit-answer-btn"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Answer'}
                </Button>
              )}
              
              {questionFeedback && !isLastQuestion && (
                <Button 
                  className="flex-1 bg-gradient-to-r from-cyan-600 to-blue-600"
                  onClick={goToNext}
                  data-testid="next-question-btn"
                >
                  Next Question <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              )}
              
              {questionFeedback && isLastQuestion && (
                <Button 
                  className="flex-1 bg-gradient-to-r from-emerald-600 to-green-600"
                  onClick={completeAssessment}
                  disabled={isSubmitting}
                  data-testid="finish-assessment-btn"
                >
                  {isSubmitting ? 'Calculating...' : 'Finish Assessment'}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
};

export default RPATAssessment;
